import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "searchResults", "selectedItemsContainer"]
  static values = { 
    searchUrl: String,
    inputName: String,
    searchKey: String
  }

  connect() {
    this.debounceTimer = null
  }

  disconnect() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer)
    }
  }

  search() {
    const query = this.inputTarget.value.trim()
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer)
    }

    // Hide results if input is empty
    if (query.length === 0) {
      this.hideResults()
      return
    }

    // Debounce the search
    this.debounceTimer = setTimeout(() => {
      this.performSearch(query)
    }, 300)
  }

  async performSearch(query) {
    try {
      const url = new URL(this.searchUrlValue, window.location.origin)
      url.searchParams.set('q', query)
      
      const response = await fetch(url, {
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })
      
      if (response.ok) {
        const items = await response.json()
        this.displayResults(items)
      }
    } catch (error) {
      console.error('Search failed:', error)
    }
  }

  displayResults(items) {
    // Filter out already selected items
    const selectedIds = this.getSelectedItemIds()
    const filteredItems = items.filter(item => !selectedIds.includes(item.id.toString()))
    
    if (filteredItems.length === 0) {
      this.searchResultsTarget.innerHTML = `<div class="p-2 text-gray-500 text-sm">No items found</div>`
    } else {
      this.searchResultsTarget.innerHTML = filteredItems.map(item => 
        `<div class="p-2 hover:bg-gray-100 cursor-pointer border-b border-gray-200 last:border-b-0" 
             data-action="click->search-list#selectItem" 
             data-item-id="${item.id}" 
             data-item-display="${item.label}">
           <div class="text-sm font-medium text-gray-900">${item.label}</div>
         </div>`
      ).join('')
    }
    this.showResults()
  }

  selectItem(event) {
    const itemId = event.currentTarget.dataset.itemId
    const itemDisplay = event.currentTarget.dataset.itemDisplay
    
    this.hideResults()
    this.addItem(itemId, itemDisplay)
  }

  addItem(itemId, itemDisplay) {
    if (!itemId) return

    const wrapperDiv = document.createElement('div')
    wrapperDiv.setAttribute('data-controller', 'search-list-remove-item')
    
    // Create a hidden field for the ids[] array
    const hiddenField = document.createElement('input')
    hiddenField.type = 'hidden'
    hiddenField.name = this.inputNameValue
    hiddenField.value = itemId
    
    // Create the turbo frame tag for the added item
    const turboFrame = document.createElement('turbo-frame')
    const frameId = `${this.searchKeyValue}_${itemId}_search_result`
    turboFrame.id = frameId
    
    // Set the src attribute of the frame to load the display content
    // Based on routes: /admin/searches/:id/display/:key
    const displayUrl = `/admin/searches/${this.searchKeyValue}/display/${itemId}`
    turboFrame.src = displayUrl
    
    // Add some loading content while the frame loads
    turboFrame.innerHTML = `<span class="text-gray-500 text-sm">Loading ${itemDisplay}...</span>`
    
    // Add the hidden field and turbo frame to the wrapper
    wrapperDiv.appendChild(hiddenField)
    wrapperDiv.appendChild(turboFrame)
    
    // Add the wrapper to the container
    this.selectedItemsContainerTarget.appendChild(wrapperDiv)
    
    // Clear the form
    this.inputTarget.value = ''
  }

  showResults() {
    this.searchResultsTarget.classList.remove('hidden')
  }

  hideResults() {
    this.searchResultsTarget.classList.add('hidden')
  }

  // Hide results when clicking outside
  clickOutside(event) {
    if (!this.element.contains(event.target)) {
      this.hideResults()
    }
  }

  // Get array of currently selected item IDs
  getSelectedItemIds() {
    const hiddenInputs = this.selectedItemsContainerTarget.querySelectorAll('input[type="hidden"]')
    return Array.from(hiddenInputs)
      .map(input => input.value)
      .filter(value => value && value !== '') // Filter out empty values
  }
}
